import { ComponentMessageMediaVO } from '@repo/types'
import { useSendMediaToServer } from '@web/modules/core'
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { mediaTypeByType } from '@repo/core'
import { useSnackbar } from '@tecsinapse/cortex-react'

interface UseUploadButtonProps {
  textInputRef?: MutableRefObject<HTMLTextAreaElement | null>
  currentFile?: ComponentMessageMediaVO
  handleUpload: (medias: ComponentMessageMediaVO[]) => void
  handleCancelUpload?: () => void
  connectionKey: string
}

export const useUploadButton = ({
  textInputRef,
  currentFile,
  handleUpload,
  handleCancelUpload,
  connectionKey
}: UseUploadButtonProps) => {
  const buttonUploadRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [showMediaOptions, setShowMediaOptions] = useState(false)
  const [files, setFiles] = useState<ComponentMessageMediaVO[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [controller, setController] = useState<AbortController>(new AbortController())
  const { uploadMedias } = useSendMediaToServer()
  const { snackbar } = useSnackbar()

  useEffect(() => {
    if (currentFile) {
      setFiles([currentFile])
    }
  }, [currentFile])

  const audioRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLInputElement>(null)
  const imageRef = useRef<HTMLInputElement>(null)
  const documentRef = useRef<HTMLInputElement>(null)

  const buttonRef = (refName: string) => {
    if (refName === 'video') {
      return videoRef
    } else if (refName === 'image') {
      return imageRef
    } else if (refName === 'audio') {
      return audioRef
    }

    return documentRef
  }

  const handleUploadButton = () => {
    setAnchorEl(buttonUploadRef.current)
    setShowMediaOptions(true)
  }

  const handleRemoveMedia = () => {
    if (handleCancelUpload) {
      setFiles([])
      handleCancelUpload()
    }
  }

  const handleMediaOptionUpload = (ref: React.RefObject<HTMLInputElement | null>) => {
    ref.current?.click()
    if (ref.current) {
      ref.current.value = ''
    }
  }

  const handleUploadFiles = useCallback(
    (files?: FileList | null) => {
      setShowMediaOptions(false)

      if (!files || files.length === 0 || !connectionKey) {
        return
      }

      const maxFiles = 5

      if (files.length > maxFiles) {
        snackbar.show('error', `Você pode anexar no máximo ${maxFiles} arquivos.`)
        return
      }

      for (let i = 0; i < files.length; i++) {
        const type = mediaTypeByType(files[i].type)

        if (!type) {
          snackbar.show('error', `O arquivo ${files[i].name} é de um tipo inválido.`)
          return
        }

        const fileExt = files[i].name.slice(files[i].name.lastIndexOf('.'))

        if (!fileExt || !type.acceptedFormats.includes(fileExt)) {
          snackbar.show('error', `O arquivo ${files[i].name} é de um tipo inválido.`)
          return
        }
      }

      setIsUploading(true)

      uploadMedias(files, connectionKey, controller.signal)
        .then(data => {
          handleUpload(data)
          setFiles(data)
        })
        .catch(error => {
          setController(new AbortController())
          snackbar.show('error', 'Erro no upload do arquivo.', { position: 'bottom-center' })
          console.error('Erro no upload do arquivo.', error)
        })
        .finally(() => {
          setIsUploading(false)
        })
    },
    [connectionKey, controller.signal, handleUpload, snackbar, uploadMedias]
  )

  const handleUploadClipboardMediaFiles = useCallback(
    (e: Event) => {
      const files = (e as ClipboardEvent)?.clipboardData?.files
      if (files && files.length > 0) {
        handleUploadFiles(files)
      }
    },
    [handleUploadFiles]
  )

  useEffect(() => {
    if (textInputRef?.current) {
      textInputRef.current.addEventListener('paste', e => {
        handleUploadClipboardMediaFiles(e)
      })
    }
  }, [handleUploadClipboardMediaFiles, textInputRef])

  return {
    buttonUploadRef,
    anchorEl,
    setAnchorEl,
    showMediaOptions,
    files,
    isUploading,
    handleUploadButton,
    handleRemoveMedia,
    buttonRef,
    handleMediaOptionUpload,
    handleUploadFiles,
    controller
  }
}
